"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Droplet, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function EliteClientInsights() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const testimonials = [{
    quote: "Benewealth didn't just fix my score; they built a",
    italicPhrase: "strategic credit architecture",
    quoteEnd: " that allowed me to secure $250k in business funding within months.",
    name: "Marcus Thorne",
    city: "New York, NY",
    date: "Nov 2024",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_marcus_thorne.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_marcus_thorne.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_marcus_thorne.png-wvc-sizes"
  }, {
    quote: "The level of professionalism is unmatched. Their",
    italicPhrase: "wealth strategy mentoring",
    quoteEnd: " transformed how I view capital. This is elite financial engineering at its finest.",
    name: "Elena Rodriguez",
    city: "Miami, FL",
    date: "Oct 2024",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_elena_rodriguez.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_elena_rodriguez.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_elena_rodriguez.png-wvc-sizes"
  }, {
    quote: "A truly bespoke experience. They handled my",
    italicPhrase: "complex funding requirements",
    quoteEnd: " with precision and discretion. The results exceeded every expectation I had.",
    name: "Julian Vane",
    city: "Chicago, IL",
    date: "Sep 2024",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_julian_vane.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_julian_vane.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_julian_vane.png-wvc-sizes"
  }, {
    quote: "The qualification process is rigorous, but the",
    italicPhrase: "access to capital",
    quoteEnd: " it unlocks is life-changing for any serious entrepreneur or business owner.",
    name: "Sarah Chen",
    city: "Austin, TX",
    date: "Aug 2024",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_sarah_chen.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_sarah_chen.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_sarah_chen.webp-wvc-sizes"
  }, {
    quote: "Finally, a firm that understands",
    italicPhrase: "high-net-worth strategy",
    quoteEnd: ". Their business credit development program is the gold standard in the industry.",
    name: "David Sterling",
    city: "Los Angeles, CA",
    date: "Jul 2024",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_david_sterling.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_david_sterling.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/client_david_sterling.webp-wvc-sizes"
  }];
  const ratingBreakdown = [{
    stars: 5,
    count: 112,
    percent: 88
  }, {
    stars: 4,
    count: 12,
    percent: 9
  }, {
    stars: 3,
    count: 3,
    percent: 3
  }, {
    stars: 2,
    count: 0,
    percent: 0
  }, {
    stars: 1,
    count: 0,
    percent: 0
  }];
  useEffect(() => {
    if (!api) return;
    if (isHovering) return;
    intervalRef.current = setInterval(() => {
      api.scrollNext();
    }, 7000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [api, isHovering]);
  return <section data-section-id="1387" id="elite-client-insights" className="bg-background text-foreground py-24 md:py-32 font-default">
      <div ref={ref} className={`mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
      transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
    }}>
        {}
        <div className="flex flex-col items-center text-center mb-16 md:mb-20">
          <div className="font-mono uppercase tracking-[0.2em] text-xs md:text-[0.75rem] text-primary mb-6">
            § 04 — client insights
          </div>
          <h2 className="font-default text-foreground" style={{
          fontSize: "clamp(2rem, 4vw, 3.5rem)",
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
          fontWeight: 500
        }}>
            The <span className="font-serif italic font-normal text-primary">Architect's</span>{" "}
            Legacy
          </h2>

          {}
          <div className="flex items-center gap-2 mt-8 mb-8">
            <div className="h-px w-24 md:w-32 bg-border" />
            <div className="flex items-center gap-0.5 text-primary">
              <div className="h-1.5 w-1.5 bg-primary rotate-45" />
              <div className="h-1.5 w-1.5 bg-primary rotate-45" />
              <div className="h-1.5 w-1.5 bg-primary rotate-45" />
            </div>
          </div>

          {}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" className="text-primary" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" className="text-primary/80" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" className="text-primary/60" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" className="text-primary/40" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span className="font-mono text-sm text-foreground uppercase tracking-wider">Verified</span>
            </div>
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-2">
              <span className="font-mono text-2xl font-medium px-3 py-0.5 rounded-md" style={{
              color: "var(--secondary-foreground)",
              backgroundColor: "var(--primary)"
            }}>
                4.9
              </span>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} data-index={i} className="h-4 w-4 fill-primary text-primary" strokeWidth={1} />)}
              </div>
            </div>
            <div className="h-4 w-px bg-border hidden sm:block" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              127 Elite Reviews
            </span>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {}
          <div className="lg:col-span-7" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
            <Carousel setApi={setApi} opts={{
            loop: true,
            align: "start"
          }} className="w-full">
              <CarouselContent className="-ml-4">
                {testimonials.map((t, i) => <CarouselItem key={i} data-index={i} className="pl-4 basis-full md:basis-1/2 lg:basis-1/2">
                    <article className="bg-secondary rounded-md p-7 md:p-8 h-full flex flex-col border border-border/50 shadow-md">
                      {}
                      <div className="flex items-center gap-2 mb-5">
                        <div className="h-px flex-1 bg-primary/30" />
                        <div className="flex items-center gap-0.5 text-primary">
                          <div className="h-1 w-1 bg-primary rotate-45" />
                        </div>
                      </div>

                      {}
                      <div className="flex items-center gap-1 mb-5">
                        {[...Array(5)].map((_, idx) => <Star key={idx} data-index={idx} className="h-4 w-4 fill-primary text-primary" strokeWidth={1} />)}
                      </div>

                      {}
                      <p className="text-foreground flex-1 mb-7 font-default" style={{
                    fontSize: "clamp(1rem, 1.1vw, 1.125rem)",
                    lineHeight: 1.6
                  }}>
                        "{t.quote}{" "}
                        <span className="font-serif italic text-primary">
                          {t.italicPhrase}
                        </span>
                        {t.quoteEnd}"
                      </p>

                      {}
                      <div className="flex items-center gap-4 pt-5 border-t border-border/30">
                        <div className="relative">
                          <div className="h-12 w-12 rounded-md overflow-hidden border border-primary/20">
                            <img src={t.avatar} data-wvc-srcset={t.avatarSrcset} data-wvc-sizes={t.avatarSizes} alt={t.name} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />
                          </div>
                          <div className="absolute -top-1 -right-1 text-primary">
                            <div className="h-2 w-2 bg-primary rotate-45" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-default font-semibold text-foreground text-base leading-tight uppercase tracking-wide">
                            {t.name}
                          </div>
                          <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground mt-1">
                            {t.city} · {t.date}
                          </div>
                        </div>
                      </div>
                    </article>
                  </CarouselItem>)}
              </CarouselContent>
            </Carousel>

            {}
            <div className="flex items-center justify-center gap-2 mt-8">
              {testimonials.map((_, i) => <Button key={i} data-index={i} onClick={() => api?.scrollTo(i)} variant="ghost" size="sm" className="h-1 w-8 rounded-md bg-border hover:bg-primary transition-all duration-300 p-0" aria-label={`Go to testimonial ${i + 1}`} />)}
            </div>
          </div>

          {}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 bg-secondary rounded-md p-7 md:p-8 border border-border/50 shadow-lg">
              <div className="font-mono uppercase tracking-[0.2em] text-xs text-primary mb-6">
                § 04.a — metrics
              </div>
              <h3 className="font-default font-semibold text-foreground mb-7 uppercase tracking-wider" style={{
              fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
              lineHeight: 1.2
            }}>
                127 Verified <span className="font-serif italic font-normal text-primary">Success Stories</span>
              </h3>

              {}
              <div className="space-y-4 mb-8">
                {ratingBreakdown.map((row, i) => <div key={i} data-index={i} className="flex items-center gap-3">
                    <div className="flex items-center gap-1 w-12 shrink-0">
                      <span className="font-mono text-sm text-foreground">
                        {row.stars}
                      </span>
                      <Star className="h-3 w-3 fill-primary text-primary" strokeWidth={1} />
                    </div>
                    <div className="flex-1 h-1.5 rounded-md bg-border/30 overflow-hidden">
                      <div className="h-full rounded-md bg-primary transition-all duration-1000 ease-out" style={{
                    width: isVisible ? `${row.percent}%` : "0%",
                    transitionDelay: `${i * 80}ms`,
                    transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
                  }} />
                    </div>
                    <span className="font-mono text-sm text-muted-foreground w-8 text-right shrink-0">
                      {row.count}
                    </span>
                  </div>)}
              </div>

              {}
              <div className="flex items-center gap-2 mb-7">
                <div className="h-px flex-1 bg-border/30" />
                <div className="flex items-center gap-0.5 text-primary">
                  <div className="h-1.5 w-1.5 bg-primary rotate-45" />
                </div>
              </div>

              {}
              <div className="grid grid-cols-2 gap-5 mb-8">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                    Average Rating
                  </div>
                  <div className="font-default text-3xl font-semibold text-foreground">
                    4.9<span className="text-primary text-xl">/5</span>
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                    Total Reviews
                  </div>
                  <div className="font-default text-3xl font-semibold text-foreground">
                    127
                  </div>
                </div>
              </div>

              {}
              <Button asChild variant="ghost" className="group h-auto p-0 bg-transparent hover:bg-transparent text-primary hover:text-foreground font-default font-medium">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span className="border-b border-current pb-0.5 uppercase tracking-widest text-xs">
                    View All Case Studies
                  </span>
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 ease-out group-hover:translate-x-1 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                </Link>
              </Button>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}